import { useEffect, useRef, useState } from 'react';
import { admin, palette, radii, typography } from '../../../../styles/tokens';
import { defaultDecisionFor } from '../shared/dedupe';
import { useImportClients } from '../shared/useImportClients';
import type {
  DedupeDecision,
  DuplicateMatch,
  ImportBatchResult,
  ImportClientInput,
  NormalizedRow,
} from '../shared/types';
import type { ImportClientsPath } from '../types';

interface ImportStepProps {
  normalized: NormalizedRow[];
  decisions: Record<string, DedupeDecision>;
  onComplete: (result: ImportBatchResult) => void;
  // The source path rows will be tagged with. Defaults to 'spreadsheet'
  // for the direct Path 3 entry. Phase 4 passes a competitor id (e.g.
  // 'square') when the user came in through a matched competitor card,
  // or 'competitor' for the "My tool isn't here" catch-all.
  sourcePath?: ImportClientsPath;
}

interface Plan {
  inserts: ImportClientInput[];
  merges: Array<{ existingClientId: string; incoming: ImportClientInput }>;
  skipped: number;
}

function buildPlan(
  normalized: NormalizedRow[],
  decisions: Record<string, DedupeDecision>,
  matches: DuplicateMatch[],
  sourcePath: ImportClientsPath,
): Plan {
  const matchByKey = new Map(matches.map(m => [m.incoming.rowKey, m]));
  const inserts: ImportClientInput[] = [];
  const merges: Plan['merges'] = [];
  let skipped = 0;

  for (const row of normalized) {
    const match = matchByKey.get(row.rowKey);
    const incoming: ImportClientInput = {
      name: row.name,
      phone: row.phone || undefined,
      email: row.email || undefined,
      source: sourcePath,
    };

    if (match) {
      const decision = decisions[row.rowKey] ?? defaultDecisionFor(match.strength);
      if (decision === 'skip') {
        skipped += 1;
        continue;
      }
      if (decision === 'merge') {
        merges.push({ existingClientId: match.existingClientId, incoming });
        continue;
      }
      // import_new falls through
    }
    inserts.push(incoming);
  }

  return { inserts, merges, skipped };
}

export default function ImportStep({
  normalized,
  decisions,
  onComplete,
  sourcePath = 'spreadsheet',
}: ImportStepProps) {
  const { findDuplicates, insertClientsBatch, mergeIntoExisting } =
    useImportClients();
  const [done, setDone] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      try {
        const matches = await findDuplicates(normalized);
        const plan = buildPlan(normalized, decisions, matches, sourcePath);
        const totalOps = plan.inserts.length + plan.merges.length;

        setTotal(totalOps);

        if (totalOps === 0) {
          onComplete({
            succeeded: 0,
            merged: 0,
            mergedFields: [],
            skipped: plan.skipped,
            failed: [],
          });
          return;
        }

        const result: ImportBatchResult = {
          succeeded: 0,
          merged: 0,
          mergedFields: [],
          skipped: plan.skipped,
          failed: [],
        };

        let completed = 0;
        const bump = () => {
          completed += 1;
          setDone(completed);
        };

        // Run batched inserts first.
        if (plan.inserts.length > 0) {
          const batchResult = await insertClientsBatch(
            plan.inserts,
            sourcePath,
            (d) => {
              // insertClientsBatch emits cumulative done/total over just its
              // own range; translate that to our global progress bar.
              setDone(d);
            },
          );
          result.succeeded += batchResult.succeeded;
          result.failed.push(...batchResult.failed);
          completed = plan.inserts.length;
          setDone(completed);
        }

        // Then run merges serially. Each is a targeted UPDATE.
        for (const m of plan.merges) {
          const mergeResult = await mergeIntoExisting(m.existingClientId, m.incoming);
          if ('error' in mergeResult) {
            result.failed.push({ row: m.incoming, error: mergeResult.error });
          } else if (mergeResult.merged) {
            result.merged += 1;
            for (const f of mergeResult.updatedFields) {
              if (!result.mergedFields.includes(f)) result.mergedFields.push(f);
            }
          } else {
            // No-op merge — every candidate field on the existing row already
            // had a value. Nothing wrong with this, but it is not a "merge"
            // in the user-visible sense, so count it under skipped.
            result.skipped += 1;
          }
          bump();
        }

        onComplete(result);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(`Import failed: ${msg}`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div
        style={{
          padding: 16,
          background: palette.redWash,
          border: `1px solid ${palette.red}`,
          borderRadius: radii.sm,
          color: palette.red,
          fontSize: typography.size.body,
        }}
      >
        {error}
      </div>
    );
  }

  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p
        style={{
          margin: 0,
          fontSize: typography.size.body,
          color: admin.body,
          lineHeight: typography.lineHeight.relaxed,
        }}
      >
        Processing {total} client{total === 1 ? '' : 's'}. This usually takes
        a few seconds.
      </p>

      <div
        style={{
          height: 10,
          background: palette.g150,
          borderRadius: radii.full,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: palette.accent,
            transition: 'width 150ms ease',
          }}
        />
      </div>

      <div style={{ fontSize: typography.size.caption, color: admin.caption }}>
        {done} / {total} ({pct}%)
      </div>
    </div>
  );
}
